import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_PROJECT_MONITORS } from '../../query/monitor/keys';
import { getMonitorsForProject } from '../../../services/monitor/prompt/PromptService';
import { MonitorPrompt } from '../../../models/monitor/MonitorPrompt';
import { useProjectSocket } from '../../../hooks/socket/UseProjectSocket';
import { invalidateProjectQueries } from '../../helpers/monitor/InvalidateProjectQueries';

const POLL_INTERVAL_MS = 60_000;
const COLLECTING_STATUSES = new Set(['PENDING', 'REFRESHING']);

export const useGetMonitorsForProject = (projectId: number | null) => {
  const queryClient = useQueryClient();
  const queryEnabled = !!projectId;

  const {
    data: monitorPrompts,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<MonitorPrompt[]>({
    queryKey: [GET_PROJECT_MONITORS, projectId],
    queryFn: () => getMonitorsForProject(projectId!),
    staleTime: 0,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.length === 0) return false;
      const isCollecting = data.some((m) => COLLECTING_STATUSES.has(m.status));
      return isCollecting ? POLL_INTERVAL_MS : false;
    },
  });

  useProjectSocket({
    projectId,
    onProjectStatsUpdate: (updatedProjectId) => {
      invalidateProjectQueries(queryClient, updatedProjectId);
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: [GET_PROJECT_MONITORS, projectId],
      refetchType: 'active',
    });

  return {
    monitorPrompts: queryEnabled ? monitorPrompts ?? null : [],
    isLoading: queryEnabled && isLoading,
    isError: queryEnabled && isError,
    error: queryEnabled ? error : null,
    refetch,
    invalidate,
  };
};
